// Polling hook for "live-ish" portal pages. One fetcher, an interval, and
// three behaviors that matter more than the interval itself:
//   - pause when the tab is hidden (visibilitychange)
//   - refetch immediately on focus / visibility-restored
//   - cancel in-flight requests on unmount or re-fetch
//
// Plus a small backoff: 3 consecutive errors → double the interval, capped
// at 5 minutes. Resets on the first success.
//
// Server data updates only as fast as the snapshot timer writes it. Faster
// polling here doesn't help; slower polling means stale numbers. 60s is a
// reasonable default for high-touch pages, 5m for pages that only change at
// period close. See docs/frontend.md for per-page cadences.

import { useCallback, useEffect, useRef, useState } from 'react';

const ERROR_BACKOFF_THRESHOLD = 3;
const ERROR_BACKOFF_CAP_MS = 5 * 60 * 1000;

export interface PollingResult<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  refresh: () => void;
}

export interface PollingOptions {
  intervalMs: number;
  /** Re-create the polling loop when any of these change (like useEffect). */
  deps?: ReadonlyArray<unknown>;
  /** Set false to suspend polling without unmounting (e.g. role gating). */
  enabled?: boolean;
}

export function usePolling<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  opts: PollingOptions,
): PollingResult<T> {
  const { intervalMs, deps = [], enabled = true } = opts;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Refs survive renders without re-triggering effects.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveErrorsRef = useRef<number>(0);
  const mountedRef = useRef<boolean>(true);

  const cancelInFlight = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const fetchOnce = useCallback(async () => {
    cancelInFlight();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const result = await fetcherRef.current(ctrl.signal);
      if (ctrl.signal.aborted || !mountedRef.current) return;
      setData(result);
      setError(null);
      consecutiveErrorsRef.current = 0;
    } catch (e) {
      if (ctrl.signal.aborted || !mountedRef.current) return;
      // Abort-named errors aren't real failures; ignore them silently.
      if (e && typeof e === 'object' && (e as { name?: string }).name === 'AbortError') return;
      consecutiveErrorsRef.current += 1;
      setError(e);
    } finally {
      if (mountedRef.current && !ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  const computeDelay = () => {
    const n = consecutiveErrorsRef.current;
    if (n < ERROR_BACKOFF_THRESHOLD) return intervalMs;
    // After threshold, each extra failure doubles the wait. Capped.
    const multiplier = 2 ** (n - ERROR_BACKOFF_THRESHOLD + 1);
    return Math.min(intervalMs * multiplier, ERROR_BACKOFF_CAP_MS);
  };

  const scheduleNext = useCallback(() => {
    clearTimer();
    if (document.visibilityState === 'hidden') return;  // paused
    timerRef.current = setTimeout(async () => {
      await fetchOnce();
      scheduleNext();
    }, computeDelay());
  }, [fetchOnce, intervalMs]);

  const refresh = useCallback(() => {
    consecutiveErrorsRef.current = 0;
    void (async () => {
      await fetchOnce();
      scheduleNext();
    })();
  }, [fetchOnce, scheduleNext]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setLoading(false);
      return () => { mountedRef.current = false; };
    }

    setLoading(true);
    void (async () => {
      await fetchOnce();
      scheduleNext();
    })();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Came back to the tab — fetch now, don't wait for the next tick.
        void (async () => {
          await fetchOnce();
          scheduleNext();
        })();
      } else {
        clearTimer();
        cancelInFlight();
      }
    };
    const onFocus = () => {
      // window 'focus' covers cases visibilitychange misses (e.g. multi-monitor).
      if (document.visibilityState === 'visible') {
        void (async () => {
          await fetchOnce();
          scheduleNext();
        })();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      clearTimer();
      cancelInFlight();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, ...deps]);

  return { data, error, loading, refresh };
}
